import { useEffect } from 'react';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import VorteileSection from '@/components/VorteileSection';
import SchaubildSection from '@/components/SchaubildSection';
import AnwendungsfaelleSection from '@/components/AnwendungsfaelleSection';
import KundenlogoSlider from '@/components/KundenlogoSlider';
import DemoSection from '@/components/DemoSection';
import FaqSection from '@/components/FaqSection';
import PricingSection from '@/components/PricingSection';
import Footer from '@/components/Footer';
import { useHash } from '@/contexts/HashContext';

const Index = () => {
  const { currentHash } = useHash();
  
  // Log when Index component renders
  console.log("Index component rendering");
  
  // Handle hash navigation for scrolling
  useEffect(() => {
    console.log("Index component mounted");
    
    if (currentHash) {
      console.log("Index: Received hash change, trying to scroll to", currentHash);
      
      try {
        // Try to decode the URI component if needed
        let targetId = currentHash;
        try {
          // If the hash is encoded, decode it
          if (currentHash.includes('%')) {
            targetId = decodeURIComponent(currentHash);
          }
        } catch (e) {
          console.error("Error decoding hash, using as-is:", e);
        }
        
        console.log("Index: Looking for element with ID", targetId);
        
        // First close all open collapsibles to prevent layout issues
        const closeAllCollapsibles = () => {
          document.querySelectorAll('[data-state="open"]').forEach((el) => {
            const parent = el.closest('[data-radix-collapsible-root]');
            if (parent) {
              const trigger = parent.querySelector('[data-radix-collapsible-trigger]');
              if (trigger && trigger instanceof HTMLElement) {
                trigger.click();
              }
            }
          });
        };
        
        // Give the DOM time to be ready before attempting to scroll
        // Use a longer timeout to ensure all animations are complete
        setTimeout(() => {
          // Close all open collapsibles
          closeAllCollapsibles();
          
          // Wait for collapsibles to close before scrolling
          setTimeout(() => {
            const element = document.getElementById(targetId);
            if (element) {
              console.log("Index: Found element, scrolling to", targetId);
              
              // Special handling for the "vorteile" section to position the heading at the top
              if (targetId === 'vorteile') {
                // Find the main Vorteile heading
                const mainHeading = document.querySelector('#vorteile h2.main-vorteile-heading');
                if (mainHeading && mainHeading instanceof HTMLElement) {
                  console.log("Index: Found main vorteile heading, scrolling to it");
                  
                  const headingPosition = mainHeading.getBoundingClientRect().top;
                  const offsetPosition = headingPosition + window.pageYOffset - 100; // Small offset from the top
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                } else {
                  // Fallback if heading not found
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - 600; // Larger offset to try to position the heading
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              } else {
                // Normal scrolling for other sections
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - 200;
                
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            } else {
              console.log("Index: Element not found for ID", targetId);
              
              // Try with the raw hash as fallback
              if (targetId !== currentHash) {
                const alternativeElement = document.getElementById(currentHash);
                if (alternativeElement) {
                  console.log("Index: Found element with non-decoded hash", currentHash);
                  
                  // Apply special handling for the vorteile section
                  if (currentHash === 'vorteile') {
                    // Find the main Vorteile heading
                    const mainHeading = document.querySelector('#vorteile h2.main-vorteile-heading');
                    if (mainHeading && mainHeading instanceof HTMLElement) {
                      const headingPosition = mainHeading.getBoundingClientRect().top;
                      const offsetPosition = headingPosition + window.pageYOffset - 100;
                      
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                      });
                    } else {
                      const elementPosition = alternativeElement.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - 600;
                      
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                      });
                    }
                  } else {
                    const elementPosition = alternativeElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - 200;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }
              }
            }
          }, 300); // Increased wait time after closing collapsibles
        }, 300); // Increased initial delay
      } catch (error) {
        console.error("Error handling hash scroll:", error);
      }
    }
  }, [currentHash]);
  
  return (
    <div className="min-h-screen w-screen bg-gray-100 overflow-x-hidden">
      <Navbar />
      <main className="w-full overflow-x-hidden">
        <HeroSection />
        <VorteileSection />
        <SchaubildSection />
        <AnwendungsfaelleSection />
        <KundenlogoSlider />
        <DemoSection />
        <FaqSection />
        <PricingSection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
